/**
 * The dashboard's session brain.
 *
 * Login strategy is ENV-dependent because cross-app silent SSO only works when
 * the Auth0 domain is *same-site* with the app:
 *
 *  - **prod** has a custom domain (`login.authe.io`) that is same-site with the
 *    apps (`*.authe.io`), so we use the shared embedded login site
 *    (`external-site`): the dashboard bounces there, and afterwards mints its
 *    own token from the tenant SSO session via a (first-party) silent iframe.
 *  - **local + sandbox** have NO custom domain — Auth0 lives at the canonical
 *    `tenant….us.auth0.com`, which is cross-site to the apps. A hidden
 *    `prompt=none` iframe would carry a third-party cookie that modern browsers
 *    block, so cross-origin silent SSO can't work. There we drive Auth0
 *    Universal Login directly (`spa-redirect`): the dashboard does a first-party
 *    top-level redirect to Auth0, which silently SSO's off the shared tenant
 *    session after the first interactive login, and lands the code (+ refresh
 *    token) back on THIS origin.
 *
 * Everything else (callback handling, token refresh, the redirect loop breaker,
 * claims → TenantContext, minimal-token permission resolution against
 * {@link policySource}) is the framework-agnostic `SessionManager` from
 * `@authe/fe-commons/session`, with React bindings via `@authe/ui-core/session`.
 */
import {
  ImpersonationClient,
  SessionManager,
  createAuth0SpaClient,
} from "@authe/fe-commons/session";
import { buildAuth0Config } from "@authe/fe-commons/auth";
import { auth0Config, urlContext, policySource, appEnv } from "@/config";
import { consumeHandoff, redeemHandoff } from "./impersonation-handoff";
import { clearTxCache } from "./txCache";

/**
 * Set when an impersonation handoff was presented and refused, so the shell can
 * say what happened instead of showing a bare login screen. Read once, at boot.
 */
let handoffFailure: string | null = null;

export function impersonationHandoffFailure(): string | null {
  return handoffFailure;
}

/**
 * Build a session from an impersonation handoff, or null when there isn't one.
 *
 * A failure here is deliberately NOT fatal: the app still boots, into the
 * ordinary unauthenticated state, with {@link impersonationHandoffFailure}
 * explaining why. Refusing to start would turn an expired handoff — the
 * commonest case, since they die in minutes — into a blank page.
 */
async function createImpersonatedSessionManager(): Promise<SessionManager | null> {
  const consumed = consumeHandoff();
  if (!consumed) return null;

  try {
    const redemption = await redeemHandoff(consumed.handoff, {
      apiBaseDomain: appEnv.apiBaseDomain,
      redeemUrl: consumed.redeemUrl,
    });
    return new SessionManager({
      // No Auth0 client at all: the session was minted server-side through
      // Custom Token Exchange, so there is nothing to redirect to, refresh or
      // call back into. See ImpersonationClient for what it refuses to do.
      client: new ImpersonationClient(redemption.tokens),
      loginStrategy: "spa-redirect",
      urlContext,
      policySource,
      // Deliberately NOT restoring a deep link: an impersonated session should
      // open where the operator can see what it is, not deep inside the
      // customer's account on whatever page they last had open.
      restoreDeepLink: false,
      onIdentityChange: clearTxCache,
    });
  } catch (error) {
    handoffFailure =
      error instanceof Error ? error.message : "This impersonation handoff is no longer valid.";
    return null;
  }
}

export async function createSessionManager(): Promise<SessionManager> {
  // Before anything else: an operator arriving from the admin portal carries a
  // handoff in the URL fragment, and has no session of their own here.
  const impersonated = await createImpersonatedSessionManager();
  if (impersonated) return impersonated;

  // Only prod has a same-site custom Auth0 domain → only there is the embedded
  // login site + cross-origin silent iframe viable. Local/sandbox use Auth0
  // Universal Login directly.
  const useEmbeddedLoginSite = appEnv.env === "prd";
  const loginStrategy = useEmbeddedLoginSite ? "external-site" : "spa-redirect";

  const client = await createAuth0SpaClient(buildAuth0Config(auth0Config), {
    useRefreshTokens: true,
    cacheLocation: "localstorage",
    // The iframe fallback is only first-party (and therefore only works) for the
    // same-domain prod login site. Off elsewhere so a tokenless first load fails
    // fast and redirects to Auth0 instead of hanging on a blocked iframe.
    useRefreshTokensFallback: useEmbeddedLoginSite,
  });

  if (useEmbeddedLoginSite) {
    // Prime from the cross-origin SSO session the login site just established so
    // the manager doesn't bounce back to it when a session already exists. No
    // SSO yet → throws → the manager redirects to the login site as intended.
    try {
      await client.getTokenSilently();
    } catch {
      /* no SSO session yet → fall through */
    }
  }

  return new SessionManager({
    client,
    loginStrategy,
    urlContext,
    policySource,
    // Restore the deep link the user started on after returning from login.
    restoreDeepLink: true,
    // Erase the identity's on-disk transaction bodies before the browser leaves
    // for a new one. Wired here rather than at the sign-out buttons because a
    // button added later that forgets would leave the previous user's decoded
    // transactions readable to whoever opens the browser next.
    onIdentityChange: clearTxCache,
  });
}
